import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  Input,
  Badge,
  Button,
  FormGroup,
  Label,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { useTicketsList } from './useTickets';
import { TicketDetailDrawer } from './TicketDetailDrawer';

const STATUS_COLORS = {
  open: 'primary',
  pending: 'warning',
  resolved: 'success',
  closed: 'secondary',
};

const PRIORITY_COLORS = {
  low: 'secondary',
  normal: 'info',
  high: 'warning',
  urgent: 'danger',
};

const STATUS_OPTIONS = [
  { value: 'open,pending', label: 'Aktiv (open + pending)' },
  { value: 'open', label: 'Nur open' },
  { value: 'pending', label: 'Nur pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'Alle' },
];

const PRIORITY_OPTIONS = ['', 'low', 'normal', 'high', 'urgent'];
const ASSIGNEE_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'me', label: 'Mir zugewiesen' },
  { value: 'unassigned', label: 'Nicht zugewiesen' },
];

/**
 * Tickets-Inbox.
 *
 * Spec: lmm-wiki/wiki/orbit-specs/orbit-tickets.md
 * Backend: orbit.api.valitype.net (Postgres)
 *
 * Cursor-Pagination — kein offset, kein totalCount-Display.
 * Trigram-Search im Backend; 300ms-Debounce auf dem Input.
 */
export function TicketsPage() {
  const { brandSlug, ticketId: ticketIdFromUrl } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('open,pending');
  const [priority, setPriority] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // URL ist source-of-truth für den geöffneten Drawer — Row-Click
  // navigiert zu /:brand/tickets/:id, Close navigiert zurück. Damit ist
  // jeder Ticket-Link teilbar und Reload öffnet exakt dasselbe Ticket.
  const openId = ticketIdFromUrl ?? null;
  const closeDrawer = () => {
    if (ticketIdFromUrl) navigate(`/${brandSlug}/tickets`, { replace: true });
  };

  // 300ms debounce, sonst feuert jeder Anschlag einen Trigram-Search-Call
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const query = useTicketsList({ status, priority, assignedTo, search });
  const items = query.data?.items ?? [];
  const nextCursor = query.data?.nextCursor ?? null;

  return (
    <div>
      <PageHeader
        title="Tickets"
        subtitle={`Customer-Support-Inbox · ${items.length} sichtbar${
          nextCursor ? ' (weitere via „Mehr laden")' : ''
        }`}
      />

      <Card className="mb-3">
        <CardBody className="py-2">
          <div className="d-flex flex-wrap gap-3 align-items-end">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Status</Label>
              <Input
                type="select"
                bsSize="sm"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{ minWidth: 220 }}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Priority</Label>
              <Input
                type="select"
                bsSize="sm"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                style={{ minWidth: 140 }}
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p || 'all'} value={p}>{p || 'Alle'}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Assignee</Label>
              <Input
                type="select"
                bsSize="sm"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                style={{ minWidth: 180 }}
              >
                {ASSIGNEE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="mb-0" style={{ flexGrow: 1, minWidth: 240 }}>
              <Label className="small text-muted mb-1">Suche</Label>
              <Input
                type="search"
                bsSize="sm"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Subject / Ticket-Nummer / Customer-Mail"
              />
            </FormGroup>
            {(status !== 'open,pending' || priority || assignedTo || search) && (
              <Button
                size="sm"
                color="secondary"
                outline
                onClick={() => {
                  setStatus('open,pending');
                  setPriority('');
                  setAssignedTo('');
                  setSearchInput('');
                  setSearch('');
                }}
              >
                Filter zurücksetzen
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && items.length === 0}
        emptyMessage="Keine Tickets für die aktuellen Filter."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 160 }}>Ref</th>
            <th>Subject</th>
            <th style={{ width: 200 }}>Customer</th>
            <th style={{ width: 110 }}>Status</th>
            <th style={{ width: 100 }}>Priority</th>
            <th style={{ width: 160 }}>Assignee</th>
            <th style={{ width: 140 }}>Letzte Aktivität</th>
          </tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr
              key={t.id}
              role="button"
              onClick={() => navigate(`/${brandSlug}/tickets/${encodeURIComponent(t.id)}`)}
              style={{ cursor: 'pointer' }}
            >
              <td><code className="small">{t.ticketNumber}</code></td>
              <td>{t.subject}</td>
              <td className="small">
                {t.customer?.displayName ?? t.customer?.email ?? '—'}
                {t.customer?.email && (
                  <div className="text-muted">{t.customer.email}</div>
                )}
              </td>
              <td>
                <Badge color={STATUS_COLORS[t.status] ?? 'secondary'} pill>
                  {t.status}
                </Badge>
              </td>
              <td>
                <Badge color={PRIORITY_COLORS[t.priority] ?? 'secondary'} pill>
                  {t.priority}
                </Badge>
              </td>
              <td className="small">
                {t.assignedTo?.displayName ?? (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td>
                {t.lastActivityAt ? (
                  <RelativeTime iso={t.lastActivityAt} />
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      {nextCursor && (
        <div className="text-center my-3">
          <Button
            color="secondary"
            outline
            size="sm"
            disabled
            title="Cursor-Pagination wird in PR2 verdrahtet (Drawer-Iteration)."
          >
            Mehr laden (Cursor: {nextCursor.slice(0, 10)}…)
          </Button>
        </div>
      )}

      <TicketDetailDrawer
        ticketId={openId}
        isOpen={!!openId}
        onClose={closeDrawer}
      />
    </div>
  );
}

